import { produce as applyImmutably } from 'immer';
import { AsyncStatus, Presence } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import { Channel } from 'lib-shared-types-channels';
import { handleActions } from 'redux-actions';

import { userModalActions } from 'components/usersModal';

import { presenceActions } from '../actions/presenceActions';

// Destructure the Presence actions.
export const {
  presenceGetAll,
  presenceGetAllSucceeded,
  presenceGetAllFailed,
  presenceSetStatus,
  presenceSetStatusSucceeded,
  presenceSetStatusFailed,

  presenceSetMediaChannel,
  presenceSetMediaChannelSucceeded,
  presenceSetMediaChannelFailed,

  presenceSetMedia,
  presenceSetMediaSucceeded,
  presenceSetMediaFailed,

  presenceSetSimultaneousRinging,
  presenceSetSimultaneousRingingSucceeded,
  presenceSetSimultaneousRingingFailed,
  presenceGet,
  presenceGetSucceeded,
  presenceGetFailed,
  presenceWsAgentStatusChanged,
  presenceWsSetOwnStatus,
  presenceWsAgentAdded,
  presenceShowOffline,
  presenceSetOthersSimultaneousRinging,
  presenceSetOthersSimultaneousRingingSucceeded,
  presenceSetOthersSimultaneousRingingFailed,
} = presenceActions;

export type UserPresence = {
  presence: Presence;
  user: User;
};

export type PresenceGroup = Array<UserPresence>;

export type PresenceStateT = {
  meta: {
    getAsyncStatus: AsyncStatus;
    setAsyncStatus: AsyncStatus;
    simultaneousAsyncStatus: AsyncStatus;
    voiceAsyncStatus: AsyncStatus;
    emailAsyncStatus: AsyncStatus;
    chatAsyncStatus: AsyncStatus;
    messagingAsyncStatus: AsyncStatus;
    toggleSimultaneousRing: AsyncStatus;
    errors: string[] | null;
    offline: boolean;
  };
  presence: Presence;
  allPresence: PresenceGroup;
};

export type PresenceState = PresenceStateT;

function initPresence(): Presence {
  return {
    last_seen: null,
    status: 'offline',
    connection_status: 'offline',
    presence_status: {
      presence_status_category: 'unknown',
      load_descriptions: [],
      simultaneous_ringing: false,
      custom_away_status_id: null,
      status_updated_at: null,
    },
    working_channels: [
      {
        channel: 'pstn_phone' as Channel,
        working: false,
      },
      {
        channel: 'widget_chat' as Channel,
        working: false,
      },
      {
        channel: 'messenger' as Channel,
        working: false,
      },
      {
        channel: 'voicemail' as Channel,
        working: false,
      },
      {
        channel: 'email' as Channel,
        working: false,
      },
      {
        channel: 'contact_form' as Channel,
        working: false,
      },
      {
        channel: 'callback' as Channel,
        working: false,
      },
      {
        channel: 'facebook_messenger' as Channel,
        working: false,
      },
      {
        channel: 'whats_app' as Channel,
        working: false,
      },
    ],
  };
}

// Init state for the Presence.
export const initialState: PresenceStateT = {
  meta: {
    getAsyncStatus: 'succeeded',
    setAsyncStatus: 'succeeded',
    voiceAsyncStatus: 'succeeded',
    emailAsyncStatus: 'succeeded',
    chatAsyncStatus: 'succeeded',
    messagingAsyncStatus: 'succeeded',
    simultaneousAsyncStatus: 'succeeded',
    toggleSimultaneousRing: 'succeeded',
    errors: null,
    offline: false,
  },
  presence: initPresence(),
  allPresence: [],
};

// Reducer
const presence = handleActions<PresenceStateT, any>(
  {
    [presenceSetOthersSimultaneousRinging.toString()]: (
      state: PresenceState,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, toggleSimultaneousRing: 'loading' },
      };
    },

    [presenceSetOthersSimultaneousRingingSucceeded.toString()]: (
      state: PresenceState,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, toggleSimultaneousRing: 'succeeded' },
      };
    },

    [presenceSetOthersSimultaneousRingingFailed.toString()]: (
      state: PresenceState,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, toggleSimultaneousRing: 'failed' },
      };
    },

    // USER UPDATE.
    [userModalActions.user.update.fulfilled.type]: (
      state: PresenceState,
      action,
    ) => {
      return applyImmutably(state, (draft: PresenceState) => {
        const user = Object.assign(
          {},
          (action as ReturnType<typeof userModalActions.user.update.fulfilled>)
            .payload.data,
        );
        const draftUserPresence = draft.allPresence.find(
          (userPresence: UserPresence) => userPresence.user.id === user.id,
        );

        if (draftUserPresence) {
          draftUserPresence.user = user;
        }
      });
    },

    // PRESENCE.
    [presenceGet.toString()]: (state: PresenceState) => {
      return { ...state, meta: { ...state.meta, getAsyncStatus: 'loading' } };
    },

    [presenceGetSucceeded.toString()]: (state: PresenceState, action: any) => {
      const agentPresence = action.payload.data;

      if (agentPresence) {
        const presence = agentPresence.presence;
        const allPresence = state.allPresence.slice();
        const presenceIndex = allPresence.findIndex(
          (userPresence: UserPresence) =>
            userPresence.user.id === agentPresence.user.id,
        );

        if (presenceIndex === -1) {
          allPresence.push(agentPresence);
        } else {
          allPresence[presenceIndex] = agentPresence;
        }

        return {
          ...state,
          meta: { ...state.meta, getAsyncStatus: 'succeeded', offline: false },
          presence:
            // Do not update if isOwnAgent flag is provided and false to maintain compatibility.
            action.payload.user_id.isOwnAgent === false
              ? state.presence
              : presence,
          allPresence,
        };
      }

      return state;
    },

    // PRESENCE.
    [presenceGetAll.toString()]: (state: PresenceState) => {
      return { ...state, meta: { ...state.meta, getAsyncStatus: 'loading' } };
    },

    [presenceGetAllSucceeded.toString()]: (
      state: PresenceState,
      action: any,
    ) => {
      // We find ourself in the presence returned.
      const user_id = action.payload.user_id;
      const agentPresence = action.payload.data.find(
        (userPresence: UserPresence) => userPresence.user.id === user_id,
      );

      if (agentPresence) {
        const presence = agentPresence.presence;
        const allPresence = action.payload.data;
        return {
          ...state,
          meta: { ...state.meta, getAsyncStatus: 'succeeded', offline: false },
          allPresence: allPresence,
          presence: presence,
        };
      }
      return state;
    },

    [presenceWsAgentAdded.toString()]: (state: PresenceState, action: any) => {
      const presence = initPresence();
      const userPresence = {
        user: action.payload.params.agent,
        presence: presence,
      };
      return { ...state, allPresence: [...state.allPresence, userPresence] };
    },

    [presenceGetAllFailed.toString()]: (state: PresenceState) => {
      return {
        ...state,
        meta: { ...state.meta, getAsyncStatus: 'failed' },
        allPresence: [],
        presence: initPresence(),
      };
    },

    [presenceSetStatus.toString()]: (state: PresenceState) => {
      return { ...state, meta: { ...state.meta, setAsyncStatus: 'loading' } };
    },

    [presenceSetStatusSucceeded.toString()]: (
      state: PresenceState,
      action: any,
    ) => {
      const userId = action.payload.params.user_id;
      const allPresence = state.allPresence.map((userPresence) =>
        userPresence.user.id === userId
          ? {
              ...userPresence,
              presence: {
                ...userPresence.presence,
                presence_status: {
                  ...userPresence.presence.presence_status,
                  presence_status_category:
                    action.payload.data.presence_status_category,
                  load_descriptions: action.payload.data.load_descriptions,
                  simultaneous_ringing:
                    action.payload.data.simultaneous_ringing,
                  custom_away_status_id:
                    action.payload.data.custom_away_status_id,
                  status_updated_at: action.payload.data.status_updated_at,
                },
              },
            }
          : userPresence,
      );

      return {
        ...state,
        allPresence,
        meta: { ...state.meta, setAsyncStatus: 'succeeded', offline: false },
        presence: {
          ...state.presence,
          status: action.payload.data.presence_status_category,
          presence_status: {
            ...state.presence.presence_status,
            presence_status_category:
              action.payload.data.presence_status_category,
            load_descriptions: action.payload.data.load_descriptions,
            simultaneous_ringing: action.payload.data.simultaneous_ringing,
            custom_away_status_id: action.payload.data.custom_away_status_id,
            status_updated_at: action.payload.data.status_updated_at,
          },
        },
      };
    },

    [presenceSetStatusFailed.toString()]: (state: PresenceState) => {
      return { ...state, meta: { ...state.meta, setAsyncStatus: 'failed' } };
    },

    [presenceSetMediaChannel.toString()]: (
      state: PresenceState,
      action: any,
    ) => {
      let voiceAsyncStatus = state.meta.voiceAsyncStatus;
      let emailAsyncStatus = state.meta.emailAsyncStatus;
      let chatAsyncStatus = state.meta.chatAsyncStatus;
      let messagingAsyncStatus = state.meta.messagingAsyncStatus;

      if (action.payload.channel === 'email') {
        emailAsyncStatus = 'loading';
      } else if (action.payload.channel === 'widget_chat') {
        chatAsyncStatus = 'loading';
      } else if (action.payload.channel === 'pstnphone') {
        voiceAsyncStatus = 'loading';
      } else if (action.payload.channel === 'facebookmessenger') {
        messagingAsyncStatus = 'loading';
      }

      return {
        ...state,
        meta: {
          ...state.meta,
          voiceAsyncStatus: voiceAsyncStatus,
          emailAsyncStatus: emailAsyncStatus,
          chatAsyncStatus: chatAsyncStatus,
          messagingAsyncStatus: messagingAsyncStatus,
        },
      };
    },

    [presenceSetMediaChannelSucceeded.toString()]: (
      state: PresenceState,
      action: any,
    ) => {
      const workingChannels = state.presence.working_channels.slice();
      let voiceAsyncStatus = state.meta.voiceAsyncStatus;
      let emailAsyncStatus = state.meta.emailAsyncStatus;
      let chatAsyncStatus = state.meta.chatAsyncStatus;
      let messagingAsyncStatus = state.meta.messagingAsyncStatus;

      const workingMediasIndex = workingChannels.findIndex(
        (workingChannel: any) =>
          workingChannel.conversation_channel ===
          action.payload.data.conversation_channel,
      );

      if (workingMediasIndex > -1) {
        workingChannels[workingMediasIndex] = {
          ...workingChannels[workingMediasIndex],
          working: action.payload.data.working,
        };
      }

      if (action.payload.data.channel === 'email') {
        emailAsyncStatus = 'succeeded';
      } else if (action.payload.data.channel === 'widget_chat') {
        chatAsyncStatus = 'succeeded';
      } else if (action.payload.data.channel === 'pstn_phone') {
        voiceAsyncStatus = 'succeeded';
      } else if (action.payload.data.channel === 'facebookmessenger') {
        messagingAsyncStatus = 'succeeded';
      }

      return {
        ...state,
        meta: {
          ...state.meta,
          voiceAsyncStatus: voiceAsyncStatus,
          emailAsyncStatus: emailAsyncStatus,
          chatAsyncStatus: chatAsyncStatus,
          messagingAsyncStatus: messagingAsyncStatus,
        },
        presence: { ...state.presence, working_channels: workingChannels },
      };
    },

    [presenceSetMediaChannelFailed.toString()]: (state: PresenceState) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          voiceAsyncStatus: 'failed',
          emailAsyncStatus: 'failed',
          chatAsyncStatus: 'failed',
          messagingAsyncStatus: 'failed',
        },
      };
    },

    [presenceSetMedia.toString()]: (state: PresenceState, action: any) => {
      let voiceAsyncStatus = state.meta.voiceAsyncStatus;
      let emailAsyncStatus = state.meta.emailAsyncStatus;
      let chatAsyncStatus = state.meta.chatAsyncStatus;
      let messagingAsyncStatus = state.meta.messagingAsyncStatus;

      if (action.payload.media === 'email') {
        emailAsyncStatus = 'loading';
      } else if (action.payload.media === 'interactivechat') {
        chatAsyncStatus = 'loading';
      } else if (action.payload.media === 'speak') {
        voiceAsyncStatus = 'loading';
      } else if (action.payload.media === 'messaging') {
        messagingAsyncStatus = 'loading';
      }

      return {
        ...state,
        meta: {
          ...state.meta,
          voiceAsyncStatus: voiceAsyncStatus,
          emailAsyncStatus: emailAsyncStatus,
          chatAsyncStatus: chatAsyncStatus,
          messagingAsyncStatus: messagingAsyncStatus,
        },
      };
    },

    [presenceSetMediaSucceeded.toString()]: (
      state: PresenceState,
      action: any,
    ) => {
      const media = action.payload.data.media;
      const working = action.payload.data.working;
      const workingChannels = state.presence.working_channels.slice();

      let voiceAsyncStatus = state.meta.voiceAsyncStatus;
      let emailAsyncStatus = state.meta.emailAsyncStatus;
      let chatAsyncStatus = state.meta.chatAsyncStatus;
      let messagingAsyncStatus = state.meta.messagingAsyncStatus;
      let updatedWorkingChannels: any[] = [];

      if (media === 'speak') {
        voiceAsyncStatus = 'succeeded';
        updatedWorkingChannels = workingChannels.map((workingChannel: any) => {
          if (
            workingChannel.conversation_channel === 'voicemail' ||
            workingChannel.conversation_channel === 'callback' ||
            workingChannel.conversation_channel === 'pstnphone'
          ) {
            return { ...workingChannel, working };
          }
          return workingChannel;
        });
      } else if (media === 'interactivechat') {
        chatAsyncStatus = 'succeeded';
        updatedWorkingChannels = workingChannels.map((workingChannel: any) => {
          if (
            ['widgetchat', 'messenger'].includes(
              workingChannel.conversation_channel,
            )
          ) {
            return { ...workingChannel, working };
          }
          return workingChannel;
        });
      } else if (media === 'email') {
        emailAsyncStatus = 'succeeded';
        updatedWorkingChannels = workingChannels.map((workingChannel: any) => {
          if (
            workingChannel.conversation_channel === 'email' ||
            workingChannel.conversation_channel === 'contactform'
          ) {
            return { ...workingChannel, working };
          }
          return workingChannel;
        });
      } else if (media === 'messaging') {
        messagingAsyncStatus = 'succeeded';
        updatedWorkingChannels = workingChannels.map((workingChannel: any) => {
          if (
            ['facebookmessenger', 'whatsapp'].includes(
              workingChannel.conversation_channel,
            )
          ) {
            return { ...workingChannel, working };
          }
          return workingChannel;
        });
      }

      return {
        ...state,
        meta: {
          ...state.meta,
          voiceAsyncStatus: voiceAsyncStatus,
          emailAsyncStatus: emailAsyncStatus,
          chatAsyncStatus: chatAsyncStatus,
          messagingAsyncStatus: messagingAsyncStatus,
        },
        presence: {
          ...state.presence,
          working_channels: updatedWorkingChannels,
        },
      };
    },

    [presenceSetMediaFailed.toString()]: (state: PresenceState) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          voiceAsyncStatus: 'failed',
          emailAsyncStatus: 'failed',
          chatAsyncStatus: 'failed',
          messagingAsyncStatus: 'failed',
        },
      };
    },

    [presenceSetSimultaneousRingingSucceeded.toString()]: (
      state: PresenceState,
      action: any,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, toggleSimultaneousRing: 'succeeded' },
        presence: {
          ...state.presence,
          presence_status: {
            ...state.presence.presence_status,
            simultaneous_ringing: action.payload.data.enabled,
          },
        },
      };
    },

    [presenceSetSimultaneousRingingFailed.toString()]: (
      state: PresenceState,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, toggleSimultaneousRing: 'failed' },
      };
    },

    [presenceWsAgentStatusChanged.toString()]: (
      state: PresenceState,
      action: any,
    ) => {
      const user_id = action.payload.params.user_id;
      const allPresence = state.allPresence.slice();

      const agentPresence: any = allPresence.find(
        (userPresence: UserPresence) => userPresence.user.id === user_id,
      );

      if (agentPresence !== undefined) {
        const newAgentPresence = {
          ...agentPresence,
          presence: {
            ...agentPresence.presence,
            status: action.payload.params.new_status, // update the status.
            presence_status: {
              ...agentPresence.presence.presence_status_category,
              presence_status_category:
                action.payload.params.presence.presence_status_category,
              load_descriptions:
                action.payload.params.presence.load_descriptions,
              simultaneous_ringing:
                action.payload.params.presence.simultaneous_ringing,
              custom_away_status_id:
                action.payload.params.presence.custom_away_status_id,
              status_updated_at:
                action.payload.params.presence.status_updated_at,
            },
            last_seen: action.payload.params.last_seen,
            working_channels: action.payload.params.working_channels,
          },
        };

        return {
          ...state,
          allPresence: state.allPresence.map((userPresence) =>
            userPresence.user.id === action.payload.params.user_id
              ? newAgentPresence
              : userPresence,
          ),
        };
      }
      return state;
    },

    [presenceWsSetOwnStatus.toString()]: (
      state: PresenceState,
      action: any,
    ) => {
      const user_id = action.payload.params.user_id;
      const allPresence = state.allPresence.slice();

      const agentPresence = allPresence.find(
        (userPresence: UserPresence) => userPresence.user.id === user_id,
      );

      if (agentPresence !== undefined) {
        const newAgentPresence = {
          ...agentPresence,
          presence: {
            ...agentPresence.presence,
            status: action.payload.params.new_status, // update the status.
            presence_status: {
              ...agentPresence.presence.presence_status,
              presence_status_category:
                action.payload.params.presence.presence_status_category,
              load_descriptions:
                action.payload.params.presence.load_descriptions,
              simultaneous_ringing:
                action.payload.params.presence.simultaneous_ringing,
              custom_away_status_id:
                action.payload.params.presence.custom_away_status_id,
              status_updated_at:
                action.payload.params.presence.status_updated_at,
            },
            working_channels: action.payload.params.working_channels,
            last_seen: action.payload.params.last_seen,
          },
        };

        return {
          ...state,
          allPresence: state.allPresence.map((userPresence: UserPresence) =>
            userPresence.user.id === action.payload.params.user_id
              ? newAgentPresence
              : userPresence,
          ),
          presence: {
            ...state.presence,
            status: action.payload.params.new_status,
            last_seen: action.payload.params.last_seen,
            presence_status: {
              ...state.presence.presence_status,
              presence_status_category:
                action.payload.params.presence.presence_status_category,
              load_descriptions:
                action.payload.params.presence.load_descriptions,
              simultaneous_ringing:
                action.payload.params.presence.simultaneous_ringing,
            },
            working_channels: action.payload.params.working_channels,
          },
        };
      }
      return state;
    },

    [presenceShowOffline.toString()]: (state: PresenceState) => {
      return {
        ...state,
        allPresence: [],
        presence: initPresence(),
        meta: { ...state.meta, offline: true },
      };
    },
  },
  initialState,
);

export default presence;
